/**
 * Owner: Masa Bayu
 * File: become-author-labels.ts
 * Date:Time: 2026-10-21 20:15
 * Description: §452 — label dua bahasa (en/ms) untuk skrin "Become an author".
 *   Semua teks paparan di sini; komponen tidak mengandungi string.
 */

import type { MarketplaceLanguage } from '../../home/marketplace/marketplace-locale';

export interface BecomeAuthorStep {
  readonly title: string;
  readonly sub: string;
}

export interface BecomeAuthorLabels {
  readonly eyebrow: string;
  readonly title: string;
  readonly sub: string;
  readonly stepsTitle: string;
  readonly steps: readonly BecomeAuthorStep[];
  readonly formTitle: string;
  readonly formSub: string;
  readonly sessionTitle: string;
  readonly sessionSub: string;
  readonly sessionEmailLabel: string;
  readonly emailLabel: string;
  readonly passwordLabel: string;
  readonly passwordHint: string;
  readonly submit: string;
  readonly createSubmit: string;
  readonly submitting: string;
  readonly errorEmail: string;
  readonly errorPassword: string;
  readonly errorEmailTaken: string;
  readonly errorInvalid: string;
  readonly errorUnavailable: string;
  readonly errorBadCredentials: string;
  readonly errorForbidden: string;
  readonly successTitle: string;
  readonly successSub: string;
  readonly accountIdLabel: string;
  readonly openEditor: string;
  readonly goDashboard: string;
  readonly ariaSteps: string;
  readonly ariaForm: string;
}

const EN: BecomeAuthorLabels = {
  eyebrow: 'For writers',
  title: 'Become an author',
  sub: 'Publish your book on QX in three steps. You keep every edition under your own account.',
  stepsTitle: 'How it works',
  steps: [
    {
      title: 'Import your PDF',
      sub: 'Studio extracts every page into an editable QXW manuscript.',
    },
    {
      title: 'Compile to QXP',
      sub: 'One click builds the frozen QXP archive, pages gated by DRM.',
    },
    {
      title: 'Publish and sell',
      sub: 'Your book goes live on QX; readers buy and read in QXReader.',
    },
  ],
  formTitle: 'Use your QX account',
  formSub:
    'Sign in with the same email and password you already registered as a reader. Do not create a second account.',
  sessionTitle: 'Continue with this account',
  sessionSub:
    'You are already signed in. Becoming an author upgrades this same QX account — no new email.',
  sessionEmailLabel: 'Signed in as',
  emailLabel: 'Email',
  passwordLabel: 'Password',
  passwordHint: 'At least 8 characters.',
  submit: 'Become an author',
  createSubmit: 'I am new to QX — create this account',
  submitting: 'Working…',
  errorEmail: 'Enter a valid email address.',
  errorPassword: 'Password must be at least 8 characters.',
  errorEmailTaken: 'This email is already registered. Sign in with that password instead.',
  errorInvalid: 'The publishing hub rejected these details. Check email and password.',
  errorUnavailable: 'The publishing hub is unreachable right now. Try again shortly.',
  errorBadCredentials: 'That email or password is wrong.',
  errorForbidden: 'This account cannot become a publisher.',
  successTitle: 'You are a publisher now',
  successSub: 'Studio uses this same email and password. Open it and press Publish on a compiled book.',
  accountIdLabel: 'Publisher ID',
  openEditor: 'Open Studio',
  goDashboard: 'Go to author dashboard',
  ariaSteps: 'Publishing steps',
  ariaForm: 'Publisher registration',
};

const MS: BecomeAuthorLabels = {
  eyebrow: 'Untuk penulis',
  title: 'Jadi Penulis',
  sub: 'Terbitkan buku anda di QX dalam tiga langkah. Setiap edisi kekal di bawah akaun anda.',
  stepsTitle: 'Cara ia berfungsi',
  steps: [
    {
      title: 'Import PDF anda',
      sub: 'Studio mengekstrak setiap halaman kepada manuskrip QXW yang boleh disunting.',
    },
    {
      title: 'Kompilasi ke QXP',
      sub: 'Satu klik membina arkib QXP beku, halaman dikawal DRM.',
    },
    {
      title: 'Terbit dan jual',
      sub: 'Buku anda tampil di QX; pembaca beli dan baca dalam QXReader.',
    },
  ],
  formTitle: 'Guna akaun QX anda',
  formSub:
    'Log masuk dengan e-mel dan kata laluan yang sama yang sudah didaftar sebagai pembaca. Jangan buat akaun kedua.',
  sessionTitle: 'Teruskan dengan akaun ini',
  sessionSub:
    'Anda sudah log masuk. Jadi penulis menaik taraf akaun QX yang sama — bukan e-mel baharu.',
  sessionEmailLabel: 'Log masuk sebagai',
  emailLabel: 'E-mel',
  passwordLabel: 'Kata laluan',
  passwordHint: 'Sekurang-kurangnya 8 aksara.',
  submit: 'Jadi Penulis',
  createSubmit: 'Saya baharu di QX — cipta akaun ini',
  submitting: 'Sedang diproses…',
  errorEmail: 'Masukkan alamat e-mel yang sah.',
  errorPassword: 'Kata laluan mesti sekurang-kurangnya 8 aksara.',
  errorEmailTaken: 'E-mel ini sudah berdaftar. Log masuk dengan kata laluan itu.',
  errorInvalid: 'Hub penerbitan menolak maklumat ini. Semak e-mel dan kata laluan.',
  errorUnavailable: 'Hub penerbitan tidak dapat dihubungi sekarang. Cuba lagi sebentar.',
  errorBadCredentials: 'E-mel atau kata laluan itu salah.',
  errorForbidden: 'Akaun ini tidak boleh menjadi penerbit.',
  successTitle: 'Anda kini penerbit',
  successSub:
    'Studio memakai e-mel dan kata laluan yang sama. Buka Studio, kemudian tekan Publish pada buku yang telah dikompilasi.',
  accountIdLabel: 'ID Penerbit',
  openEditor: 'Buka Studio',
  goDashboard: 'Ke dashboard penulis',
  ariaSteps: 'Langkah penerbitan',
  ariaForm: 'Pendaftaran penerbit',
};

export function resolveBecomeAuthorLabels(language: MarketplaceLanguage): BecomeAuthorLabels {
  return language === 'ms' ? MS : EN;
}
